import { fromDate, ZonedDateTime } from '@internationalized/date';
import { getHistogramBarInterval } from '../histogram.utils';
import { HistogramDataPoint } from './histogramBars';

export const getUnloadedRegions = (
  loadedTimeRanges: [ZonedDateTime, ZonedDateTime][],
  startTime: ZonedDateTime,
) => {
  if (!loadedTimeRanges || loadedTimeRanges.length === 0) {
    // If no loaded ranges, return a large unloaded region
    // Use earliest and latest dates from loaded ranges if available, otherwise use current time
    const now = startTime; // Use the component's timezone context
    const oneYearAgo = now.subtract({ years: 1 });
    const oneYearFromNow = now.add({ years: 1 });
    return [{ start: oneYearAgo, end: oneYearFromNow }];
  }

  const regions: { start: ZonedDateTime; end: ZonedDateTime }[] = [];
  const sortedRanges = [...loadedTimeRanges].sort(
    (a, b) => a[0].toDate().getTime() - b[0].toDate().getTime(),
  );

  // Find the earliest and latest points in all loaded ranges
  const earliestLoaded = sortedRanges[0][0];
  const latestLoaded = sortedRanges[sortedRanges.length - 1][1];

  // Extend beyond the loaded ranges by a significant amount
  const rangeMs =
    latestLoaded.toDate().getTime() - earliestLoaded.toDate().getTime();
  const extensionMs = Math.max(rangeMs * 10, 30 * 24 * 60 * 60 * 1000); // At least 30 days

  const extendedStart = earliestLoaded.subtract({
    milliseconds: extensionMs,
  });
  const extendedEnd = latestLoaded.add({ milliseconds: extensionMs });

  // Check if there's an unloaded region before the first loaded range
  if (earliestLoaded.compare(extendedStart) > 0) {
    regions.push({ start: extendedStart, end: earliestLoaded });
  }

  // Check for gaps between loaded ranges
  for (let i = 0; i < sortedRanges.length - 1; i++) {
    const currentEnd = sortedRanges[i][1];
    const nextStart = sortedRanges[i + 1][0];
    if (currentEnd.compare(nextStart) < 0) {
      regions.push({ start: currentEnd, end: nextStart });
    }
  }

  // Check if there's an unloaded region after the last loaded range
  if (latestLoaded.compare(extendedEnd) < 0) {
    regions.push({ start: latestLoaded, end: extendedEnd });
  }

  return regions;
};

export const findBarFromMousePosition = (
  mouseX: number,
  mouseY: number,
  canvasRef: React.RefObject<HTMLCanvasElement>,
  endTime: ZonedDateTime,
  startTime: ZonedDateTime,
  dragOffset: number,
  data: HistogramDataPoint[] | undefined,
  maxValue: number | undefined,
  maxBarHeight: number,
  isBarLoaded: (
    intervalStart: ZonedDateTime,
    intervalEnd: ZonedDateTime,
  ) => boolean,
) => {
  const canvas = canvasRef.current;
  if (!canvas) return { bar: null, index: null, barCenterX: null };

  const rect = canvas.getBoundingClientRect();
  const canvasX = mouseX - rect.left;
  const canvasY = mouseY - rect.top;
  const canvasWidth = rect.width;

  // Calculate time range metrics
  const visibleDurationMs =
    endTime.toDate().getTime() - startTime.toDate().getTime();
  const pixelsPerMs = canvasWidth / visibleDurationMs;

  // Calculate the same interval as used in rendering
  const intervalMs = getHistogramBarInterval(visibleDurationMs);

  // Convert canvas position to time, accounting for drag offset
  const timeOffsetMs = (canvasX - dragOffset) / pixelsPerMs;
  const targetTime = startTime.add({ milliseconds: timeOffsetMs });

  // Align to interval boundary (same as rendering logic)
  const alignedStartMs =
    Math.floor(targetTime.toDate().getTime() / intervalMs) * intervalMs;

  const intervalStart = fromDate(new Date(alignedStartMs), startTime.timeZone);
  const intervalEnd = intervalStart.add({ milliseconds: intervalMs });

  // Create data map (same as rendering)
  const dataByTime = new Map<number, HistogramDataPoint>();
  if (data) {
    data.forEach((point) => {
      const pointIntervalStart =
        Math.floor(point.startTime.toDate().getTime() / intervalMs) *
        intervalMs;
      dataByTime.set(pointIntervalStart, point);
    });
  }

  // Check if we have data for this interval
  const dataPoint = dataByTime.get(alignedStartMs);

  if (dataPoint && isBarLoaded(intervalStart, intervalEnd)) {
    // Calculate bar height to check if mouse is over the bar
    const actualMaxValue =
      maxValue ?? (data ? Math.max(...data.map((d) => d.value), 1) : 1);
    const barHeightScale = maxBarHeight / actualMaxValue;
    const barHeight = dataPoint.value * barHeightScale;
    const barY = maxBarHeight - barHeight;

    // Check if mouse Y is within the bar
    if (canvasY >= barY) {
      // Calculate bar center X position for tooltip
      const barStartMs =
        intervalStart.toDate().getTime() - startTime.toDate().getTime();
      const barEndMs =
        intervalEnd.toDate().getTime() - startTime.toDate().getTime();
      const barCenterMs = (barStartMs + barEndMs) / 2;
      const barCenterX = barCenterMs * pixelsPerMs + dragOffset + rect.left;

      return {
        bar: dataPoint,
        index: data ? data.indexOf(dataPoint) : -1,
        barCenterX,
      };
    }
  }

  return { bar: null, index: null, barCenterX: null };
};

export const calculateSelectionBounds = (
  isSelecting: boolean,
  selectionStartTime: ZonedDateTime | null,
  selectionEndTime: ZonedDateTime | null,
  canvasRef: React.RefObject<HTMLCanvasElement>,
  endTime: ZonedDateTime,
  startTime: ZonedDateTime,
  dragOffset: number,
) => {
  if (!isSelecting || !selectionStartTime || !selectionEndTime) {
    return null;
  }

  const canvas = canvasRef.current;
  if (!canvas) return null;

  const rect = canvas.getBoundingClientRect();
  const canvasWidth = rect.width;

  // Calculate time range metrics
  const visibleDurationMs =
    endTime.toDate().getTime() - startTime.toDate().getTime();
  const pixelsPerMs = canvasWidth / visibleDurationMs;

  // Calculate the same interval as used in rendering
  const intervalMs = getHistogramBarInterval(visibleDurationMs);

  // Get the range of selected times
  const rangeStartTime =
    selectionStartTime.compare(selectionEndTime) < 0
      ? selectionStartTime
      : selectionEndTime;
  const rangeEndTime =
    selectionStartTime.compare(selectionEndTime) < 0
      ? selectionEndTime
      : selectionStartTime;

  // Snap to interval boundaries
  const alignedStartMs =
    Math.floor(rangeStartTime.toDate().getTime() / intervalMs) * intervalMs;
  const alignedEndMs =
    Math.ceil(rangeEndTime.toDate().getTime() / intervalMs) * intervalMs;

  // Calculate the visual bounds of the selection
  const startMs = alignedStartMs - startTime.toDate().getTime();
  const endMs = alignedEndMs - startTime.toDate().getTime();

  const left = startMs * pixelsPerMs + dragOffset;
  const width = (endMs - startMs) * pixelsPerMs;

  return { left, width };
};

export const paintBars = (
  canvasRef: React.RefObject<HTMLCanvasElement>,
  endTime: ZonedDateTime,
  startTime: ZonedDateTime,
  dragOffset: number,
  data: HistogramDataPoint[] | undefined,
  maxValue: number | undefined,
  maxBarHeight: number,
  hoveredBar: HistogramDataPoint | null,
  barColor: string,
) => {
  const canvas = canvasRef.current;
  if (!canvas) return;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // Get canvas dimensions
  const rect = canvas.getBoundingClientRect();
  const canvasWidth = rect.width;
  const canvasHeight = maxBarHeight;

  // Set canvas resolution to match display size
  const devicePixelRatio = window.devicePixelRatio || 1;
  canvas.width = canvasWidth * devicePixelRatio;
  canvas.height = canvasHeight * devicePixelRatio;
  ctx.scale(devicePixelRatio, devicePixelRatio);

  // Calculate time range metrics
  const visibleDurationMs =
    endTime.toDate().getTime() - startTime.toDate().getTime();
  const pixelsPerMs = canvasWidth / visibleDurationMs;

  // Use provided max value or calculate from data
  const actualMaxValue =
    maxValue ?? (data ? Math.max(...data.map((d) => d.value), 1) : 1);
  const barHeightScale = canvasHeight / actualMaxValue;

  // Clear canvas
  ctx.clearRect(0, 0, canvasWidth, canvasHeight);

  // Account for drag offset to determine actual visible area
  const dragOffsetMs = -dragOffset / pixelsPerMs;
  const actualVisibleStart = startTime.add({ milliseconds: dragOffsetMs });
  const actualVisibleEnd = endTime.add({ milliseconds: dragOffsetMs });

  // Calculate appropriate interval for the visible time range
  const intervalMs = getHistogramBarInterval(visibleDurationMs);

  // Generate all time intervals in the visible range (with buffer)
  const bufferMs = visibleDurationMs * 0.5; // 50% buffer each side
  const renderStartTime = actualVisibleStart.subtract({
    milliseconds: bufferMs,
  });
  const renderEndTime = actualVisibleEnd.add({ milliseconds: bufferMs });

  // Align start time to interval boundary using the histogram's timezone
  const alignedStartMs =
    Math.floor(renderStartTime.toDate().getTime() / intervalMs) * intervalMs;

  // Convert the aligned start milliseconds into a ZonedDateTime in the same timezone
  let currentIntervalStart = fromDate(
    new Date(alignedStartMs),
    startTime.timeZone,
  );

  // Create a map of data points by time for quick lookup
  const dataByTime = new Map<number, HistogramDataPoint>();
  if (data) {
    data.forEach((point) => {
      // Use the interval start time as the key
      const intervalStart =
        Math.floor(point.startTime.toDate().getTime() / intervalMs) *
        intervalMs;
      dataByTime.set(intervalStart, point);
    });
  }

  // Render bars for each interval
  while (
    currentIntervalStart.toDate().getTime() < renderEndTime.toDate().getTime()
  ) {
    const intervalEnd = currentIntervalStart.add({
      milliseconds: intervalMs,
    });

    // Calculate bar position
    const barStartMs =
      currentIntervalStart.toDate().getTime() - startTime.toDate().getTime();
    const x = barStartMs * pixelsPerMs + dragOffset;
    const width = (intervalMs / visibleDurationMs) * canvasWidth;

    // Skip if completely off-screen
    if (x + width < -50 || x > canvasWidth + 50) {
      currentIntervalStart = intervalEnd;
      continue;
    }

    // Add spacing between bars
    const spacing = Math.min(2, width * 0.1);
    const adjustedX = x + spacing / 2;
    const adjustedWidth = Math.max(1, width - spacing);

    // Check if we have data for this interval
    const dataPoint = dataByTime.get(currentIntervalStart.toDate().getTime());

    if (dataPoint) {
      // Render data bar
      const height = dataPoint.value * barHeightScale;
      const y = canvasHeight - height;

      // Check if this bar is hovered - compare aligned interval times
      const isHovered =
        hoveredBar &&
        Math.floor(hoveredBar.startTime.toDate().getTime() / intervalMs) *
          intervalMs ===
          currentIntervalStart.toDate().getTime();

      if (isHovered) {
        // Use highlight color based on dark mode
        const isDarkMode = document.documentElement.classList.contains('dark');
        ctx.fillStyle = isDarkMode ? '#bfdbfe' : '#1d4ed8'; // blue-200 : blue-700
      } else {
        ctx.fillStyle = barColor;
      }

      ctx.fillRect(adjustedX, y, adjustedWidth, height);
    }

    currentIntervalStart = intervalEnd;
  }
};
